import Vue from "vue"
import api from "@/api"
import userService from "@/services/userService"
import { store } from "@/store/stores"

const RIGHTS_RECHECK_TIMEOUT_MS = 10000

// Never persist this grant: cached rights may belong to a previous login.
class TrainingFeatureAccess {
  constructor() {
    this.state = Vue.observable({ allowed: false, ready: false, userId: "", revision: 0 })
    this.token = ""
    this.listeners = new Set()
    this.requestVersion = 0
    this.rejectionCheck = null
    this.handleRefresh = () => this.refresh()
    this.handleVisible = () => { if (document.visibilityState === "visible") this.refresh() }
  }

  identity() {
    try { return { userId: String(userService.getUserId() || ""), token: localStorage.getItem("token") || "" } }
    catch (_) { return { userId: "", token: "" } }
  }

  isAllowed() {
    const { userId, token } = this.identity()
    return this.state.allowed && this.state.userId === userId && this.token === token && Boolean(userId && token)
  }

  capture() { return this.isAllowed() ? this.state.revision : null }
  isCurrent(revision) { return revision !== null && revision === this.state.revision && this.isAllowed() }

  reject(revision, error) {
    if (!this.isCurrent(revision) || ![401, 403].includes(error?.response?.status)) return Promise.resolve(false)
    if (this.rejectionCheck?.revision === revision) return this.rejectionCheck.promise
    const check = { ...this.identity(), revision, version: ++this.requestVersion, promise: null }
    this.rejectionCheck = check
    check.promise = this.recheckRejectedRequest(check)
    return check.promise
  }

  async recheckRejectedRequest(check) {
    let timer
    let rights = []
    try {
      // A workout or studio rejection does not prove that stat_admin was removed.
      // Keep the verified grant while checking the authoritative rights endpoint.
      rights = await Promise.race([
        Promise.resolve().then(() => api.getUserRights()),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("Rights check timed out")), RIGHTS_RECHECK_TIMEOUT_MS) }),
      ])
    } catch (_) {
      // After an explicit rejection, failed revalidation must not retain access.
      rights = []
    } finally {
      clearTimeout(timer)
      if (this.rejectionCheck === check) this.rejectionCheck = null
    }
    if (check.version === this.requestVersion && this.isCurrent(check.revision)) {
      // An unchanged grant does not notify subscribers or restart the failed sync.
      this.resolve(check.userId, check.token, rights)
    }
    return true
  }

  subscribe(listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  apply(userId, token, allowed, ready) {
    const changed = this.state.userId !== userId || this.token !== token || this.state.allowed !== allowed || this.state.ready !== ready
    this.token = token
    Object.assign(this.state, { userId, allowed, ready })
    if (!changed) return
    this.state.revision++
    this.listeners.forEach((listener) => { Promise.resolve().then(listener).catch(() => {}) })
  }

  // Called with the rights fetched for this exact authenticated session.
  resolve(userId, token, rights) {
    const current = this.identity()
    if (!userId || !token || current.userId !== String(userId) || current.token !== token) return
    this.requestVersion++
    this.apply(String(userId), token, Array.isArray(rights) && rights.includes("stat_admin"), true)
  }

  invalidate() {
    this.requestVersion++
    this.apply("", "", false, false)
  }

  clear() {
    this.requestVersion++
    this.apply("", "", false, true)
  }

  async refresh() {
    const { userId, token } = this.identity()
    if (!userId || !token) { this.clear(); return }
    if (this.rejectionCheck && this.isCurrent(this.rejectionCheck.revision)) return this.rejectionCheck.promise
    if (this.state.userId !== userId || this.token !== token) this.invalidate()
    const version = ++this.requestVersion
    try {
      const rights = await api.getUserRights()
      if (version === this.requestVersion) this.resolve(userId, token, rights)
    } catch (error) {
      // A network outage cannot grant access. A previously verified session may
      // remain usable offline; an explicit rejection revokes it immediately.
      if (version === this.requestVersion && [401, 403].includes(error?.response?.status)) this.resolve(userId, token, [])
    }
  }

  start() {
    if (this.unsubscribe) return
    this.unsubscribe = store.subscribe((mutation) => {
      const current = this.identity()
      if (this.state.ready && (current.userId !== this.state.userId || current.token !== this.token)) this.invalidate()
      if (mutation.type === "userStore/setUserRights" && !mutation.payload?.includes("stat_admin")) {
        this.resolve(current.userId, current.token, [])
      }
    })
    window.addEventListener("online", this.handleRefresh)
    window.addEventListener("focus", this.handleRefresh)
    window.addEventListener("storage", this.handleRefresh)
    document.addEventListener("visibilitychange", this.handleVisible)
    // App.initialize supplies a fresh grant after token renewal. Until then,
    // neither persisted Vuex rights nor localStorage can enable these features.
  }

  stop() {
    this.unsubscribe?.(); this.unsubscribe = null
    window.removeEventListener("online", this.handleRefresh)
    window.removeEventListener("focus", this.handleRefresh)
    window.removeEventListener("storage", this.handleRefresh)
    document.removeEventListener("visibilitychange", this.handleVisible)
    this.invalidate()
  }
}

export default new TrainingFeatureAccess()
